import axios from 'axios';
import { MessageBox } from 'element-ui'
import store from '@/store/index.js';
import router from '@/router';
import {
  PAGE_TYPE,
  getRoutePageType,
  getLoginRouteByPageType
} from '@/common/pageRouteUtils'
import { redirectToMpReauth } from '@/common/h5MpBridge'
import EQconfig from '../config'
import {showFullScreenLoading, tryHideFullScreenLoading} from './loading'
import utils from '@/common/utils'

const API_MODE = (process.env.VUE_APP_API_MODE || 'proxy').toLowerCase()
const DIRECT_WITH_CREDENTIALS = (process.env.VUE_APP_DIRECT_WITH_CREDENTIALS || 'false').toLowerCase() === 'true'
const DIRECT_API_HOSTS = {
  Q7FileAPI: process.env.VUE_APP_DIRECT_Q7FILEAPI || 'http://lgbzbback.erqicn.cn',
  WXmpApi: process.env.VUE_APP_DIRECT_WXMPAPI || 'http://oa.erqicn.cn',
  WXSendApi: process.env.VUE_APP_DIRECT_WXSENDAPI || 'http://oa.erqicn.cn'
}
const LOGIN_ROUTE_NAMES = ['login', 'MobileLogin', 'HoreLogin']
const H5_EXPIRE_TIP = '登录已过期，请重新登录'

/** 防止并发 511 重复处理 */
let sessionExpiredHandling = false

function normalizeJoinUrl(host, path) {
  return `${host.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

function normalizeApiPrefixUrl(url = '') {
  if (!url || typeof url !== 'string') {
    return url
  }

  const trimmed = url.trim()
  if (!trimmed) {
    return trimmed
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed
  }

  const match = trimmed.match(/^\/?(Q7FileAPI|WXmpApi|WXSendApi)\/(.*)$/)
  if (!match) {
    return trimmed
  }

  return `/${match[1]}/${match[2]}`
}

function resolveDirectUrl(url = '') {
  if (!url || /^https?:\/\//i.test(url)) {
    return url
  }

  const normalizedUrl = normalizeApiPrefixUrl(url)
  const match = normalizedUrl.match(/^\/(Q7FileAPI|WXmpApi|WXSendApi)\/(.*)$/)
  if (!match) {
    return normalizedUrl
  }

  const prefix = match[1]
  const path = match[2]
  const targetHost = DIRECT_API_HOSTS[prefix]
  if (!targetHost) {
    return normalizedUrl
  }

  return normalizeJoinUrl(targetHost, path)
}

/** 浏览器直链下载等场景：与 axios 拦截器同一套地址解析（proxy 保持相对路径，direct 拼完整域名） */
export function resolveApiUrl(url = '') {
  const normalizedUrl = normalizeApiPrefixUrl(url)
  if (API_MODE === 'direct') {
    return resolveDirectUrl(normalizedUrl)
  }
  return normalizedUrl
}

/**
 * 业务会话 511：
 * - PC / 移动端：清会话后直接进对应登录页（原行为）
 * - 小程序 H5：确认后回原生换发短期 token，再打开 H5 登录
 *   （短期 token 账号登录成功后会作废，不能复用 web-view URL 旧值）
 * @returns {boolean} 是否已接管
 */
function handleSessionExpired(resData) {
  const currentRoute = router.currentRoute
  if (currentRoute && currentRoute.name === 'BigScreenFullscreen') {
    console.warn('全屏页面检测到登录过期，但不自动跳转')
    return false
  }
  if (currentRoute && LOGIN_ROUTE_NAMES.includes(currentRoute.name)) {
    return false
  }
  if (sessionExpiredHandling) {
    return true
  }
  sessionExpiredHandling = true

  store.commit('clearToken')
  store.commit('clearMenu')
  tryHideFullScreenLoading()

  const pageType = getRoutePageType(currentRoute)
  const loginRouteName = getLoginRouteByPageType(pageType)
  const redirect = currentRoute && currentRoute.fullPath ? currentRoute.fullPath : '/'

  const finish = () => {
    sessionExpiredHandling = false
  }

  if (pageType === PAGE_TYPE.H5) {
    const raw = String((resData && (resData.Erro || resData.Text)) || '').trim()
    const tip = !raw || raw === '登录身份已失效' ? H5_EXPIRE_TIP : raw

    MessageBox.alert(tip, '登录提示', {
      confirmButtonText: '我知道了',
      type: 'warning',
      customClass: 'h5-auth-expire-box',
      closeOnClickModal: false,
      closeOnPressEscape: false,
      showClose: false
    })
      .catch(() => {})
      .then(async () => {
        try {
          const jumped = await redirectToMpReauth({ redirect })
          if (!jumped) {
            await router.push({ name: loginRouteName, query: { redirect } })
          }
        } finally {
          finish()
        }
      })
    return true
  }

  console.warn('[Axios] 登录过期，跳转到登录页:', loginRouteName, 'redirect=>', redirect)
  Promise.resolve(router.push({ name: loginRouteName, query: { redirect } })).finally(finish)
  return true
}

// 使用单例实例，复用 TCP 连接，避免每次请求都触发 Initial connection（可节省约 300ms+）
const instance = axios.create({
  timeout: 15000,
  withCredentials: true
})

// 请求拦截
instance.interceptors.request.use(config => {  
    // 统一规范 API 前缀，兼容 Q7FileAPI/... 与 /Q7FileAPI/... 两种写法
    config.url = normalizeApiPrefixUrl(config.url)

    if (API_MODE === 'direct') {
      config.url = resolveDirectUrl(config.url)
      config.withCredentials = DIRECT_WITH_CREDENTIALS
    }

    const requestData = Object.assign(config.data ? config.data : {}, { macid: EQconfig.EQMac.macid });
    // 获取 apitype 参数，默认为 'default'
    const apitype = config.apitype || 'default'; 
    
    // 对GET请求特殊处理，避免CORS问题
    if (config.method === 'get') {
      config.withCredentials = false;  // GET请求不携带凭据
    }
    
    //对请求数据进行加密
    if (config.method === 'post' || config.method === 'put') {
      // 使用 utils 中的 encryptKey 方法对数据进行加密，传入 apitype 参数
      config.data = utils.encryptKey(JSON.stringify(requestData), apitype);
    } else if (config.method === 'get') {
      // GET请求不加密，直接传递参数
      config.params = requestData;
    } else if (config.method === 'delete') {
      // DELETE请求参数加密
      config.params = {
        encryptedData: utils.encryptKey(JSON.stringify(requestData), apitype)
      };
    }

    //请求数据前拦截开始调用loading方法
    if (!config.silentRefresh) {  // 根据silentRefresh配置决定是否显示loading
      showFullScreenLoading()
    }
    return config
  }, err => {
    tryHideFullScreenLoading()
    console.error('请求错误:', err);
    return Promise.reject(err);
})

// 响应拦截
instance.interceptors.response.use(res => {  
    // 获取 apitype 参数，默认为 'default'
    const apitype = res.config.apitype || 'default';
    
    // GET请求：不解密，但也要统一处理登录过期
    if (res.config.method === 'get') {
      if (!res.config.silentRefresh) {
        tryHideFullScreenLoading()
      }
      if (res.data && res.data.Status === '511') {
        handleSessionExpired(res.data)
        return Promise.reject(new Error('登录过期'))
      }
      return res.data;
    }     
    //在响应拦截器中对返回的数据进行解密（非GET请求）
    if (res.data && typeof res.data === 'string') {
      try {
        // 使用 decryptKey 方法解密响应数据，传入 apitype 参数
        const decryptedData = utils.decryptKey(res.data, apitype);
        if (!decryptedData) {
          console.error('返回数据无效');
          return res.data;
        }
        
        try {
          res.data = JSON.parse(decryptedData);
        } catch (jsonError) {
          console.error('解析失败:', jsonError);
          return res.data;
        }
      } catch (error) {
        console.error('响应数据解密失败:', error);
        // 如果解密失败，返回原始数据
        return res.data;
      }
    } 
    // else {
    //   console.log('收到未加密的响应数据:', res.data);
    // } 

    //请求完成后关闭loading
    if (!res.config.silentRefresh) {  // 根据silentRefresh配置决定是否关闭loading
      tryHideFullScreenLoading()
    }
    
    if (!res.data) {
      console.error('响应数据为空');
      return Promise.reject(new Error('响应数据为空'));
    }

    //拦截结果如果是511代表用户登录过期，清除登录信息并跳转到对应登录页（携带redirect）
    if (res.data.Status === "511") {
      if (!handleSessionExpired(res.data)) {
        return res.data
      }
      return Promise.reject(new Error('登录过期'))
    } else if (res.data.Status === "407") {      //清除用户token秘钥
      store.commit('clearToken')
      //清除用户权限功能菜单列表
      store.commit('clearMenu')
      //回到登录页面
      router.push('/error')
    }
    return res.data
  }, err => {
    //请求完成后关闭loading
    tryHideFullScreenLoading()
    
    // 如果是请求被取消的错误，不显示在控制台
    if (err.message === 'Request aborted' || err.code === 'ECONNABORTED') {
      return Promise.reject(err)
    }
    
    // 根据错误码处理对应的错误信息
    if (err && err.response) {
      switch (err.response.status) {
        case 400:
          err.message = '请求错误'
          break
        case 401:
          err.message = '未授权，请登录'
          break
        case 403:
          err.message = '拒绝访问'
          break
        case 404:
          err.message = `请求地址出错: ${err.response.config.url}`
          break
        case 408:
          err.message = '请求超时'
          break
        case 500:
          err.message = '服务器内部错误'
          break
        case 501:
          err.message = '服务未实现'
          break
        case 502:
          err.message = '网关错误'
          break
        case 503:
          err.message = '服务不可用'
          break
        case 504:
          err.message = '网关超时'
          break
        case 505:
          err.message = 'HTTP版本不受支持'
          break
        default:
          err.message = '未知错误'
      }
    }
    return Promise.reject(err)
})

export function request(config) {
  return instance(config)
}

